import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft, ArrowUpRight, Calendar, User } from "lucide-react";
import { api, newsOgImageUrl } from "@/lib/api";
import PageMeta from "@/components/PageMeta";
import ShareButton from "@/components/ShareButton";

export default function NewsDetail() {
  const { id } = useParams();
  const [article, setArticle] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    setNotFound(false);
    api.get(`/news/${id}`)
      .then((r) => setArticle(r.data))
      .catch(() => setNotFound(true));
  }, [id]);

  if (notFound) {
    return (
      <div className="pshl-container py-32 text-center">
        <div className="font-display text-3xl text-white">Article not found.</div>
        <Link to="/news" className="mt-6 inline-flex items-center gap-2 text-pshl-electric"><ArrowLeft className="h-4 w-4" /> Back to News</Link>
      </div>
    );
  }
  if (!article) {
    return <div className="pshl-container py-32 text-center text-white/60">Loading…</div>;
  }

  return (
    <div data-testid={`news-detail-${article.id}`}>
      <PageMeta
        title={article.title}
        description={article.excerpt}
        image={newsOgImageUrl(article.id)}
        imageAlt={article.title}
        type="article"
      />
      {/* Hero */}
      <section className="relative pt-20 md:pt-24 pb-6 overflow-hidden">
        <div className="absolute inset-0 bg-cover bg-center opacity-25" style={{ backgroundImage: `url(${article.image})` }} />
        <div className="absolute inset-0 bg-gradient-to-r from-[#020A1A] via-[#020A1A]/85 to-[#020A1A]/40" />
        <div className="relative pshl-container max-w-4xl">
          <Link to="/news" data-testid="back-to-news" className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.22em] font-display text-white/60 hover:text-pshl-electric mb-8">
            <ArrowLeft className="h-3.5 w-3.5" /> All News
          </Link>
          <span className="inline-block text-[10px] uppercase tracking-[0.25em] font-display font-semibold bg-pshl-electric text-white px-2 py-1">{article.category}</span>
          <h1 className="mt-5 font-display text-3xl sm:text-4xl md:text-5xl lg:text-6xl text-white leading-[0.95] tracking-tighter">{article.title}</h1>
          <div className="mt-6 flex flex-wrap items-center gap-5 gap-y-3 text-xs uppercase tracking-[0.2em] text-white/65">
            <span className="inline-flex items-center gap-2"><Calendar className="h-3.5 w-3.5 text-pshl-electric" /> {article.date}</span>
            {article.author && <span className="inline-flex items-center gap-2"><User className="h-3.5 w-3.5 text-pshl-electric" /> {article.author}</span>}
            <ShareButton kind="news" slug={article.id} title={article.title} label="Share Story" />
          </div>
        </div>
      </section>

      {/* Body */}
      <section className="section pt-4">
        <div className="pshl-container max-w-3xl">
          {(article.body || []).map((para, i) => (
            <p key={`${article.id}-p${i}-${(para || "").slice(0, 24)}`} className="text-base md:text-lg text-white/85 leading-relaxed mb-5">{para}</p>
          ))}
          <p className="text-sm uppercase tracking-[0.2em] text-pshl-electric font-display mt-12">— PSHL8 Newsroom</p>
        </div>
      </section>

      {/* Related */}
      {article.related?.length > 0 && (
        <section className="section bg-[#08090E]">
          <div className="pshl-container">
            <div className="text-xs uppercase tracking-[0.32em] text-pshl-electric font-display mb-3">More Stories</div>
            <h2 className="font-display text-3xl md:text-4xl text-white tracking-tighter mb-8">Related News.</h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
              {article.related.map((n) => (
                <Link key={n.id} to={`/news/${n.id}`} data-testid={`related-news-${n.id}`} className="group bg-pshl-surface border border-white/10 hover:border-pshl-electric transition-colors overflow-hidden block">
                  <div className="h-40 bg-cover bg-center relative" style={{ backgroundImage: `url(${n.image})` }}>
                    <div className="absolute inset-0 bg-gradient-to-t from-pshl-surface to-transparent" />
                    <span className="absolute top-3 left-3 text-[10px] uppercase tracking-[0.25em] font-display font-semibold bg-pshl-electric text-white px-2 py-1">{n.category}</span>
                  </div>
                  <div className="p-5">
                    <div className="text-[11px] uppercase tracking-[0.22em] text-white/45">{n.date}</div>
                    <h3 className="mt-2 font-display text-lg text-white group-hover:text-pshl-electric">{n.title}</h3>
                    <p className="mt-2 text-sm text-white/70 leading-relaxed">{n.excerpt}</p>
                    <div className="mt-4 inline-flex items-center gap-1 text-xs font-display uppercase tracking-[0.18em] text-white/80 group-hover:text-pshl-electric">
                      Read Story <ArrowUpRight className="h-3.5 w-3.5" />
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}
    </div>
  );
}
